import React from 'react';
import styled, { css } from 'styled-components';
import { format, isSameDay, isBefore } from 'date-fns';
import { ReactDatePickerProps } from 'react-datepicker';

import { isBetweenDates } from '../../utils';
import { CalendarProps } from '../../types';
import { getMidnightFromString } from '../../../../../../../../utils';
import { isEqual } from 'lodash';

type RenderDayContentsProps = Pick<
  CalendarProps,
  | 'startDate'
  | 'endDate'
  | 'isMobile'
  | 'excludeDates'
  | 'searchCalendarDuration'
  | 'indicationMap'
  | 'isPreset'
>;

const INDICATION_COLORS = {
  1: '#919499',
  2: '#ADD65C',
  3: '#7ACC29',
  4: '#45BD81',
};

export const renderDayContents: (
  props: RenderDayContentsProps
) => ReactDatePickerProps['renderDayContents'] =
  ({
    startDate,
    endDate,
    isMobile = false,
    excludeDates,
    searchCalendarDuration,
    indicationMap,
    // предустановлена ли дата надо разобраться нужно ли вообще
    isPreset = false,
  }) =>
  // eslint-disable-next-line react/display-name
  (day, d) => {
    const date = d as Date;

    const isEndDay = isEqual(date, endDate);
    const isStartDay = isEqual(date, startDate);
    const isInSelectedInterval = isBetweenDates(date, { start: startDate, end: endDate });

    /** смотрим входит ли ячейка в исключающие даты, засериваем, но не дизайблим */
    const isExclude = !!(
      d &&
      !isEndDay &&
      !isStartDay &&
      excludeDates?.find((excludeDate) =>
        isSameDay(new Date(getMidnightFromString(excludeDate.toString())), d)
      )
    );

    /** эта штука нужна чтобы узнать выгодная дата или нет */
    const isProfitable = Boolean(
      !startDate &&
        !endDate &&
        !isEndDay &&
        !isStartDay &&
        !isInSelectedInterval &&
        indicationMap.get(format(date, 'yyyy-MM-dd'))
    );

    const calendarDuration =
      d &&
      searchCalendarDuration?.find(
        ({ date, startDate: startDatePrice }) =>
          startDate &&
          // вытаскиваем день из массива
          isEqual(new Date(format(date, 'MM/dd/yyyy')), d) &&
          isEqual(new Date(format(startDatePrice, 'MM/dd/yyyy')), startDate) &&
          // покрываем кейс когда выбрана только дата вылета
          ((!endDate &&
            // чтобы на стартовую дату не залезали цены
            isBefore(
              new Date(format(startDate, 'MM/dd/yyyy')),
              new Date(format(date, 'MM/dd/yyyy'))
            )) ||
            // покрываем кейс когда выбраны две даты
            (endDate &&
              // а тут проверяем чтобы цена была на конечной дате и все
              isEqual(
                new Date(format(date, 'MM/dd/yyyy')),
                new Date(format(endDate, 'MM/dd/yyyy'))
              )))
      );

    const price = calendarDuration?.fullPrice;
    const priceColor = calendarDuration?.colorNumber;

    const showIndication = !isPreset;

    return (
      <StyledCell
        $startShift={isStartDay}
        $endShift={isEndDay}
        $exclude={isExclude}
        $isPreset={isPreset}
      >
        <CellContent $showIndication={showIndication}>
          {day}
          {showIndication && (
            <Price
              $isActive={isEndDay}
              $indicationType={priceColor}
              $isProfitable={isProfitable}
              $isMobile={isMobile}
            >
              {price?.toLocaleString('ru-RU', { useGrouping: true })}
              {isProfitable ? 'выгодно' : null}
            </Price>
          )}
        </CellContent>
      </StyledCell>
    );
  };

const Price = styled.div<{
  $isActive: boolean;
  $indicationType?: number;
  $isProfitable?: boolean;
  $isMobile?: boolean;
}>`
  position: absolute;
  bottom: 2px;
  left: 0;
  right: 0;
  font-weight: 400;
  font-size: 9px;
  line-height: 12px;
  text-align: center;
  height: 12px;
  width: 100%;
  color: ${({ $isActive, $indicationType, $isProfitable }) => {
    if ($isProfitable) {
      return '#02997d';
    }
    if ($isActive || !$indicationType) {
      return '#4D4D4D';
    }

    return INDICATION_COLORS[$indicationType];
  }};
`;

const CellContent = styled.span<{ $showIndication: boolean }>`
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const StyledCell = styled.div<{
  $endShift?: boolean;
  $startShift?: boolean;
  $exclude: boolean;
  $isPreset?: boolean;
}>`
  position: relative;
  height: 100%;
  box-sizing: border-box;
  ${({ $startShift, $endShift, $isPreset }) =>
    !$isPreset &&
    ($startShift || $endShift) &&
    css`
      font-weight: bold;
      color: #17181a;
      background-color: ${$startShift ? '#fffbe5' : '#cce763'};
      border: 1px solid;
      border-top: 1px solid;
      border-bottom: 1px solid;
    `};
  ${({ $exclude }) => $exclude && 'color: #c2c5cc;'}

  border-color: ${({ $startShift }) => ($startShift ? '#f8e71c' : '#6abf40')};
`;
